import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "~/utils/helpers"
import { Tooltip } from "./Tooltip"
import { X } from "@phosphor-icons/react"

const tagBase = "text-sm mr-1.5 my-1 py-0.5 px-1.5 rounded-[8px] cursor-pointer hover:*:opacity-100"
const neutralClassName =
  "border-badge-neutral-border bg-badge-neutral-bg text-badge-neutral-fg-text gap-1"

const defaultVariants = {
  default: neutralClassName,
  neutral: neutralClassName,
  green: "border-badge-green-border bg-badge-green-bg text-badge-green-fg-text gap-1",
  blue: "border-badge-blue-border bg-badge-blue-bg text-badge-blue-fg-text gap-1",
  red: "border-badge-red-border bg-badge-red-bg text-badge-red-fg-text gap-1",
  yellow: "border-badge-orange-border bg-badge-orange-bg text-badge-orange-fg-text gap-1",
  gray: "border-badge-neutral-border bg-badge-neutral-bg text-badge-neutral-fg-text gap-1",
  violet: "border-badge-violet-border bg-badge-violet-bg text-badge-violet-fg-text gap-1",
  mint: "border-transparent bg-badge-mint-bg text-badge-mint-fg-text gap-1",
  white: "border-transparent bg-base text-fg-base gap-1",
  // EMPHASIZED
  emphasizedNeutral:
    "bg-gradient-to-b from-white to-subtle dark:from-zinc-800/90 dark:to-zinc-950 text-fg-subtle shadow-borders-base gap-1",
  emphasizedGreen: "bg-badge-emphasized-green-bg text-fg-on-color border-green-700 border-1 gap-1",
  emphasizedBlue: "bg-badge-emphasized-blue-bg text-fg-on-color border-blue-700 border-1 gap-1",
  emphasizedRed: "bg-badge-emphasized-red-bg text-fg-on-color border-red-700 border-1 gap-1",
  emphasizedOrange:
    "bg-badge-emphasized-orange-bg text-fg-on-color border-orange-700 border-1 gap-1",
}

const badgeVariants = cva(
  "inline-flex items-center rounded-md border label-sm transition-colors w-fit focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        ...defaultVariants,
        fine: "border-badge-green-border bg-badge-green-bg text-badge-green-fg-text",
        reorder: "border-badge-orange-border bg-badge-orange-bg text-badge-orange-fg-text",
        stockout: "border-badge-red-border bg-badge-red-bg text-badge-red-fg-text",
        partialStockout: "border-badge-red-border bg-badge-red-bg text-badge-red-fg-text",
        overstock: "border-badge-blue-border bg-badge-blue-bg text-badge-blue-fg-text",

        draft: "border-badge-neutral-border bg-badge-neutral-bg text-badge-neutral-fg-text",
        sent: "border-badge-blue-border bg-badge-blue-bg text-badge-blue-fg-text",
        delivered: "border-badge-green-border bg-badge-green-bg text-badge-green-fg-text",
        registered: "border-badge-green-border bg-badge-green-bg text-badge-green-fg-text",

        outline: "text-foreground",
        external: "bg-subtle text-xs text-fg-subtle py-0.5 px-1.5 rounded-lg",

        tag:
          "border-transparent bg-badge-violet text-fg-violet hover:border-badge-violet-border" +
          tagBase,
        tagIsActive:
          "bg-badge-violet-selected border-badge-violet-border-selected text-fg-on-color" + tagBase,
        tagStriped: "border-dashed bg-neutral-100 text-fg-subtle border-neutral-300" + tagBase,
        systemTag: "border-mint-500/30 text-mint-500 mr-1.5 my-1 py-0.5 px-1.5 capitalize",

        pending: "border-badge-blue-border bg-badge-blue-bg text-badge-blue-fg-text",
        expired: "border-badge-red-border bg-badge-red-bg text-badge-red-fg-text",
      },
      size: {
        base: "px-2 py-1",
        sm: "px-1.5 py-0.5 h-[20px]",
        xs: "px-1 py-0.5",
      },
    },
    defaultVariants: {
      variant: "neutral",
    },
  },
)

export type BadgeVariant = VariantProps<typeof badgeVariants>["variant"]

type RemoveButtonProps = {
  selected: boolean
  onRemove: () => void
  tooltipTitle?: string
}

function RemoveButton({ selected, onRemove, tooltipTitle }: RemoveButtonProps) {
  // TODO: Make this dynamic in the future (This can be done the tailwind v4 implements a safelist so the colors doesn't get pruned)
  const gradientSelected = `bg-gradient-to-r from-transparent from-0% via-50% to-100% via-badge-violet-selected to-badge-violet-selected`
  const gradient = `bg-gradient-to-r from-transparent from-0% via-50% to-100% via-badge-violet to-badge-violet`

  return (
    <>
      <div
        className={cn(
          "opacity-0 absolute right-0 flex items-center justify-end pr-0.5",
          selected ? gradientSelected : gradient,
        )}
      >
        <div className="h-4 w-6 flex justify-end items-center">
          <Tooltip title={tooltipTitle}>
            <div>
              <X
                size={12}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  onRemove()
                }}
              />
            </div>
          </Tooltip>
        </div>
      </div>
    </>
  )
}

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  isActive?: boolean
}

/**
 * Compact pill labelling a value — status, type, or count. Inline metadata in tables/headers/rows.
 * For row lifecycle/health states with a coloured dot, use StatusBadge.
 * Canonical intent: {@link ../design-system/intents.ts BADGE_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, size = "base", isActive, ...props }, ref) => {
    if (isActive && variant === "tag") {
      variant = `${variant}IsActive`
    }

    return <div ref={ref} className={cn(badgeVariants({ variant, size }), className)} {...props} />
  },
)

Badge.displayName = "Badge"

export { Badge, RemoveButton }
